import deepequals from 'fast-deep-equal';
import { DateRange } from 'rewire-common';
import { observable } from 'rewire-core';
import { createColumn, createGrid, ICell, IColumn, IGrid, IGridOptions, IRowData } from 'rewire-grid';
import { error, Form, IFormContext, validator } from 'rewire-ui';
import { BatchFilterInput, IBatchTimecard, IResult, ScheduleFilterInput, ScheduleSort } from '../DataTypes';
import { baseUrl } from '../graphql/GraphQLClient';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import { batchSummaryRouteManager, IRouteManagerState, payRouteManager } from '../main-navigation/RouteManager';
import PagingModel from '../settings/PagingModel';
import { BatchStatusRenderer, ExportStatusRenderer } from '../timecard/CellRenderers';
import { confirmationModel } from '../ui/ConfirmationDialog';
import LinkCell from '../ui/LinkCell';
import { notifications } from '../ui/Notifications';
import { stringToYMD } from '../utility/DateUtils';
import { formatCurrency } from '../utility/Formatters';
import SelectedRowWatcher from '../utility/SelectedRowWatcher';
import batchTimecardSummaryModel from './BatchTimecardSummaryModel';

// async function downloadAndRenameFile(fileUrl: string, newFileName: string) {
//   try {
//     const response = await window.fetch(fileUrl);
//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }

//     const fileBlob = await response.blob();
//     const blobUrl = URL.createObjectURL(fileBlob);

//     const link = document.createElement('a');
//     link.href = blobUrl;
//     link.download = newFileName;

//     document.body.appendChild(link);
//     link.click();

//     document.body.removeChild(link);
//     URL.revokeObjectURL(blobUrl);
//     console.log(`File "${newFileName}" downloaded successfully.`);
//   } catch (error) {
//     console.error('Error downloading the file:', error);
//   }
// }

interface IBatchSummaryModelState {
  grid: IGrid;
  pagingModel: PagingModel;
  searchResultsMessage: string;
  selectedBatch?: IBatchTimecard;
}

class BatchSummaryModel {
  state: IBatchSummaryModelState;
  selectedRowWatcher: SelectedRowWatcher = new SelectedRowWatcher();

  constructor() {
    const emptyResult = [] as IBatchTimecard[];
    this.state = observable({
      grid: this.createGrid(emptyResult),
      pagingModel: new PagingModel(this._load),
      searchResultsMessage: '',
    });
  }

  private shouldLoad(prevState: IRouteManagerState, route: string | undefined, filters: ScheduleFilterInput | undefined): boolean {
    if (prevState.route !== route) return true;
    if (!deepequals(prevState.filters, filters)) return true;
    return false;
  }

  hasResults(results: IResult<IBatchTimecard>) {
    return results?.data?.length;
  }

  public get pagingModel() {
    return this.state.pagingModel;
  }

  public get grid() {
    return this.state.grid;
  }

  public set grid(grid: IGrid) {
    this.state.grid = grid;
  }

  public get searchResultsMessage() {
    return this.state.searchResultsMessage;
  }

  public set searchResultsMessage(message: string) {
    this.state.searchResultsMessage = message;
  }

  get selectedBatch(): IBatchTimecard | undefined {
    return this.state.selectedBatch;
  }

  set selectedBatch(batch: IBatchTimecard | undefined) {
    this.state.selectedBatch = batch;
    batchTimecardSummaryModel.loadBatch(batch);
  }

  get canDownload(): boolean {
    return this.selectedBatch !== undefined;
  }

  get canAccept(): boolean {
    return this.selectedBatch !== undefined && this.selectedBatch.status === 'closed' && mainPageModel.canEditBatches;
  }

  get canRegenerate(): boolean {
    return this.selectedBatch !== undefined && mainPageModel.canEditBatches;
  }

  get canDelete(): boolean {
    return this.selectedBatch !== undefined && mainPageModel.canDeleteBatches;
  }

  get isLoading(): boolean {
    return this.pagingModel.loading || batchSummaryRouteManager.isLoading;
  }

  load = async (prevState: IRouteManagerState) => {
    const { route, filters } = batchSummaryRouteManager;
    if (this.shouldLoad(prevState, route, filters)) {
      await this.pagingModel.load();
    }
  };

  _load = async (): Promise<{ cursor?: string; totalCount: number }> => {
    const { filters, sorts } = batchSummaryRouteManager;
    try {
      const batchIds: string[] | undefined = filters?.batchIds;
      const filter: BatchFilterInput = batchIds?.length ? { ids: batchIds } : {};
      const results: IResult<IBatchTimecard> = await DataQueries.getBatchesSummary(
        filter,
        this.pagingModel.itemsPerPage,
        this.pagingModel.currentPage,
      );
      this.searchResultsMessage = this.hasResults(results) ? '' : 'No results found that match the filter criteria';
      const rows = this.createRows(results.data, this.grid.columns, sorts);
      this.grid.set(rows);

      this.selectedRowWatcher.watch(this.grid, (row) => {
        this.selectedBatch = row;
      });

      return results.data.length ? { cursor: results.cursor, totalCount: results.totalCount } : { cursor: '', totalCount: 0 };
    } catch (ex) {
      this.searchResultsMessage = 'Error retrieving results';
      this.grid.set([]);
      return { cursor: '', totalCount: 0 };
    }
  };

  formatDateRange = (dateRange?: DateRange): string => {
    if (!dateRange) return '';
    return `${stringToYMD(dateRange.startAsString())} - ${stringToYMD(dateRange.endAsString())}`;
  };

  formatDate = (date?: string): string => {
    if (!date) return '';
    return stringToYMD(date);
  };

  makeLink = (batch: IBatchTimecard) => {
    if (!batch.effective) return undefined;
    return payRouteManager.createLinkFor({ effective: batch.effective, selectedBatchId: batch.id, selectedEmployeeId: undefined });
  };

  private get columns(): any[] {
    return [
      { name: 'id', title: 'Id', visible: false },
      {
        name: 'name',
        title: 'Name',
        renderer: LinkCell,
        type: { type: 'text', options: { to: (cell: ICell) => this.makeLink(cell.row.data) } },
      },
      { name: 'code', title: 'Code' },
      { name: 'status', title: 'Batch Status', renderer: BatchStatusRenderer, type: { options: { showLabel: true } } },
      { name: 'exportStatus', title: 'Export Status', renderer: ExportStatusRenderer, type: { options: { showLabel: true } } },
      { name: 'effective', title: 'Effective Range', map: this.formatDateRange },
      { name: 'createdDate', title: 'Created Date', map: this.formatDate },
      { name: 'createdBy', title: 'Created By' },
      { name: 'timecardCount', title: 'Timecard Count', align: 'right' },
      { name: 'gross', title: 'Gross', align: 'right', map: formatCurrency },
    ];
  }

  private createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    this.columns.forEach((col) => {
      const { name, title, ...opts } = col;
      const column = createColumn(name, title, opts);
      column.canSort = false;
      cols.push(column);
    });
    return cols;
  }

  getRowId(rowData: IBatchTimecard) {
    return rowData.id;
  }

  createRows(results: IBatchTimecard[] | undefined, cols: IColumn[], sorts?: ScheduleSort): IRowData[] {
    const rows: IRowData[] = [];

    if (results && results.length) {
      const sortedResults: IBatchTimecard[] = this.sortResults(results, sorts);

      sortedResults.forEach((row) => {
        const r: IRowData = { id: `${this.getRowId(row)}`, data: row, options: (row as any).options };
        rows.push(r);
      });
    }
    return rows;
  }

  private sortResults(rowData: IBatchTimecard[], sorts?: ScheduleSort): IBatchTimecard[] {
    if (!sorts?.batch) {
      return rowData;
    }

    return sorts.batch.direction === 'asc'
      ? rowData.sort((a, b) => a.name!.localeCompare(b.name!))
      : rowData.sort((a, b) => b.name!.localeCompare(a.name!));
  }

  private createOptions(): IGridOptions {
    return {
      clearSelectionOnBlur: false,
      readOnly: true,
      multiSelect: false,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      },
    };
  }

  private createGrid(results: IBatchTimecard[]): IGrid {
    const cols: IColumn[] = this.createColumns();
    const rows: IRowData[] = this.createRows(results, cols);
    const options: IGridOptions = this.createOptions();
    const grid: IGrid = createGrid(rows, cols, options);
    grid.rowHeight = 36;
    return grid;
  }

  public deleteBatch = async () => {
    return new Promise((resolve) => {
      if (this.selectedBatch === undefined) {
        resolve(true);
        return;
      }

      const batchName = this.selectedBatch!.name;
      const form = Form.create(
        (form: IFormContext) => ({
          confirm: form
            .string()
            .label('Batch Name')
            .validators('required', validator('==', batchName, error('Batch name does not match.')))
            .placeholder(`enter ${batchName}`)
            .autoFocus(),
        }),
        {},
        { validateOnUpdate: true, updateOnChange: true },
      );
      form.validateForm(); // Prime the form with validation errors.

      confirmationModel.load(
        `Deleting a batch can cause timecards to recalculate causing audit differences between the WorkSight Export file and results previously exported to Payroll. Proceed at your own risk.

        Please confirm you wish to delete batch ${batchName}`,
        {
          yesAction: async () => {
            if (!form.hasChanges || form.hasErrors) {
              throw new Error('confirmation validation failed');
            }
            try {
              const result = await DataMutations.deleteBatches([this.selectedBatch!.id]);
              if (result) {
                notifications.success(`${batchName} was successfuly deleted`);
                this.grid.removeSelectedRows();
                resolve(true);
                return true;
              }
            } catch (err) {}
            notifications.error(`There was an error deleting ${batchName}`);
            resolve(true);
            return true;
          },
          noAction: () => (resolve(false), true),
          yesDisabled: () => !form.hasChanges || form.hasErrors,
          maxWidth: 'sm',
          title: `Delete ${batchName}?`,
          form,
        },
      );
    });
  };

  public acceptBatch = async () => {
    return new Promise((resolve) => {
      if (this.selectedBatch === undefined) {
        resolve(true);
        return;
      }

      const batchName: string = this.selectedBatch!.name!;

      confirmationModel.load(`Please confirm you wish to accept batch ${batchName}`, {
        yesAction: async () => {
          try {
            const result = await DataMutations.exportBatch(this.selectedBatch!.id);
            if (result) {
              notifications.success(`${batchName} was successfuly accepted`);
              this.selectedBatch!.status = 'exported';
              resolve(true);
              return true;
            }
          } catch (err) {}
          notifications.error(`There was an error accepting ${batchName}`);
          resolve(true);
          return true;
        },
        noAction: () => (resolve(false), true),
        maxWidth: 'sm',
        title: `Accept ${batchName}?`,
      });
    });
  };

  public downloadBatch = async () => {
    await Promise.all([this.downloadExportFile(), this.runExportReport()]);
  };

  private async downloadExportFile() {
    if (this.selectedBatch === undefined) {
      return;
    }

    const fileResult = await DataQueries.getBatchesByIds(`exportFileUrl`, [this.selectedBatch.id]);
    if (fileResult?.length !== 1) return false;
    const fileUrl = fileResult[0]?.exportFileUrl;
    if (fileUrl) {
      window.location.href = fileUrl; // trigger download
      // window.open(url, '_'); // open in a new tab
    }
  }

  private async runExportReport() {
    if (this.selectedBatch === undefined) {
      return;
    }

    const reportResult = await DataQueries.runReport(
      { name: 'Export Report', reportId: 'S1er9zvWI8', type: 'export', title: 'Export Report' },
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { batchId: this.selectedBatch.id },
    );
    const reportUrl = `${baseUrl}/reports/${reportResult}/${this.selectedBatch.id.concat('.pdf')}`;
    window.open(reportUrl, '_'); // open in a new tab
  }

  public regenerateBatch = async () => {
    if (this.selectedBatch === undefined) {
      return;
    }

    const batchName = this.selectedBatch!.name;
    try {
      const result = await DataMutations.regenerateExportFile(this.selectedBatch!.id);
      if (result) {
        notifications.success(`${batchName} is regenerating...`);
      }
    } catch (err) {
      notifications.error(`There was an error regenerating ${batchName}`);
    }
  };
}

const batchSummaryModel = new BatchSummaryModel();
export default batchSummaryModel;
